import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import SpinnerButton from '../../components/Button/SpinnerButton';
import { Container, Row, Col, Form, Stack } from 'react-bootstrap';
import useAuthStore from '../../stores/authStore';
import useApiStore from '../../stores/apiStore';

function LoginScreen() {
    const { t } = useTranslation();
    const [error, setError] = useState();
    const [isLoading, setLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [unverifiedUser, setUnverifiedUser] = useState(null);
    const navigate = useNavigate();

    const [emailSent, setEmailSent] = useState(false);
    const { refreshEmailVerified } = useAuthStore();
    const { sendVerificationEmail } = useApiStore();

    const handleResendVerificationEmail = async () => {
        if (!unverifiedUser) return;
        setIsSendingEmail(true);
        try {
            const errorCode = await sendVerificationEmail(unverifiedUser);
            if (errorCode) {
                setError(t(errorCode));
            } else {
                setEmailSent(true);
            }
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleLogin = async (event) => {
        setError();
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget;

        if (form.checkValidity() === false) {
            setValidated(true);
            return
        }

        setLoading(true);
        const email = form.elements.inputEmail.value;
        const password = form.elements.inputPassword.value;

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            if (!user.emailVerified) {
                setUnverifiedUser(user);
                const error = new Error('Email not verified');
                error.code = 'auth/email-not-verified';
                throw error;
            }

            refreshEmailVerified();
            handleLoginSuccess();
        } catch (err) {
            const errorCode = err.code;
            if (errorCode === 'auth/invalid-email') {
                setError(t('errors.invalidEmail'));
            } else if (errorCode === 'auth/user-not-found') {
                setError(t('errors.userNotFound'));
            } else if (errorCode === 'auth/email-not-verified') {
                setError(t('errors.emailNotVerified'));
            } else {
                setError(t('errors.incorrectCredentials'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLoginSuccess = () => {
        const savedReturnUrl = sessionStorage.getItem('returnUrl') || '/';
        sessionStorage.removeItem('returnUrl');
        navigate(savedReturnUrl);
    };

    return (
        <Container className="py-5" style={{ maxWidth: '400px' }}>
            <Row className="mb-5">
                <Col xs={12} className="text-center">
                    <h2 className="title">{t('login.title')}</h2>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Form noValidate validated={validated} onSubmit={handleLogin}>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="inputEmail">{t('login.email')}</Form.Label>
                            <Form.Control
                                required
                                id="inputEmail"
                                type="email"
                                placeholder={t('login.emailPlaceholder')}
                                disabled={isLoading}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('login.emailValid')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <Form.Label htmlFor="inputPassword">{t('login.password')}</Form.Label>
                                <button type="button" onClick={() => navigate('/password/forgot')} className="mb-2 btn btn-link p-0"><small>{t('login.forgotPassword')}</small></button>
                            </div>
                            <Form.Control
                                required
                                id="inputPassword"
                                type="password"
                                placeholder={t('login.passwordPlaceholder')}
                                disabled={isLoading}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('login.passwordRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {error && (
                            <div className="mb-3">
                                <div className="text-danger mb-2">{error}</div>
                                {unverifiedUser && (
                                    emailSent ? <div className="text-success">{t('login.emailResend')}</div> :
                                        <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            onClick={handleResendVerificationEmail}
                                            disabled={isSendingEmail}
                                        >
                                            <small>{t('login.resendVerificationEmail')}</small>
                                        </button>
                                )}
                            </div>
                        )}
                        <Stack gap={3}>
                            <SpinnerButton
                                type="submit"
                                isLoading={isLoading}
                                className="w-100 bg-pink"
                                label={t('login.signIn')}
                            />
                            <SpinnerButton
                                onClick={() => navigate('/register')}
                                className="w-100"
                                label={t('login.noAccount')}
                            />
                        </Stack>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
}

export default LoginScreen;
